<template>
    <ui-modal
        icon="far fa-cog"
        name="config-date-proposal"
        title="Datas de Envio da Proposta"
        size="lg"
        :load="load"
        :loadSuccess="loadSuccess"
        :loadFail="loadFail"
    >
        <div class="content-body">
            <div>
                <h2 class="title">Datas das Parcelas</h2>
                <ui-dates-proposal-table-flow
                    :parcel-options="parcelOptions"
                    :proposal-dates-options="proposalDatesOptions"
                    :installment-list="installmentList"
                />
            </div>

            <div>
                <h2 class="title" style="margin-top: 10px">Data da Base de Resíduo</h2>
                <ui-dates-proposal-residual
                    :parcel-options="parcelOptions"
                    :proposal-dates-options="proposalDatesOptions"
                    :data="residualData"
                    :installment-list="installmentList"
                    @update-residue-date="validateForm"
                />
            </div>

            <div class="content-footer">
                <div v-if="validationErrors.length > 0" class="error-messages">
                    <div v-for="(error, index) in validationErrors" :key="index" class="error-message">
                        {{ error }}
                    </div>
                </div>
                <ui-button
                    class="btn-submit"
                    design="success md"
                    @click="submit"
                    :disabled="!isValid"
                >Salvar</ui-button
                >
            </div>
        </div>
    </ui-modal>
</template>

<script>
import { uiModal } from '@/components/ui/modal'
import { uiButton } from '@/components/ui/buttons'

import uiDatesProposalTableFlow from '../dates-proposal-table-flow'
import uiDatesProposalResidual from '../dates-proposal-residual'

import { alertMixin } from '@/mixins'
import { salesTable } from '@/services/axios'
import { mapGetters, mapActions } from 'vuex'

export default {
	name: 'dates-proposal',
	components: {
		uiModal,
		uiButton,
		uiDatesProposalTableFlow,
		uiDatesProposalResidual,
	},

	mixins: [alertMixin],

	data() {
		return {
			load: false,
			loadSuccess: false,
			loadFail: false,
			parcelOptions: [
				{ label: 'Ato', value: 'act' },
				{ label: 'Entrada', value: 'signal' },
				{ label: 'Única', value: 'unique' },
				{ label: 'Valor Fixo', value: 'fixed_value' },
				{ label: 'Mensal', value: 'monthly' },
				{ label: 'Bimestral', value: 'bimonthly' },
				{ label: 'Trimestral', value: 'quarterly' },
				{ label: 'Quadrimestral', value: 'four_monthly' },
				{ label: 'Semestral', value: 'semester' },
				{ label: 'Anual', value: 'yearly' },
				{ label: 'Intermediária', value: 'intermediary' },
				{ label: 'Chaves', value: 'keys' },
				{ label: 'FGTS', value: 'fgts' },
				{ label: 'Financiamento', value: 'financing' },
				{ label: 'Subsídio', value: 'allowance' },
				{ label: 'Bonificação', value: 'bonus' },
				{ label: 'Bem e Direito', value: 'assets_rights' },
			],
			proposalDatesOptions: [
				{ label: 'Ato da Venda', value: 'act_of_sale' },
				{ label: 'Data da Parcela', value: 'installment_date' },
				{ label: 'Período Anterior', value: 'previous_period' },
				{ label: 'Mês Anterior', value: 'previous_month' },
				{ label: 'Diferença de Meses - Ato da Venda', value: 'month_diff' },
			],
			defaultInstallment: {
				type: '',
				interest_date: '',
				interest_date_month: 0,
				interest_date_day: 0,
				readjustment_date: '',
				readjustment_date_month: 0,
				readjustment_date_day: 0,
				installment_ref: '',
			},
			defaultResidual: {
				type: 'residue_date',
				date: '',
				quantity_months: 0,
				days_of_the_month: 0,
				installment_ref: '',
			},
			isValid: false,
			errorMessages: { 
    		  missingType: 'Necessário possuir um tipo de parcela definido.',
    		  atLeastOneField: 'Informe pelo menos uma data ou parcela de referência.',
    		  invalidMonths: 'Informe a quantidade de meses',
    		  circularRefs: 'As parcelas não podem fazer referência mútua entre si.'
    		},
			validationErrors: [],
			installmentList: [],
			residualData: {},
		}
	},
	computed: {
		...mapGetters('product', {
			product: 'getProduct',
		}),
		...mapGetters('salesTable', {
			salesTable: 'getSalesTable',
		}),
	},

	watch: {
		salesTable: {
			handler(newValue) {
				if (newValue) {
					this.processSalesTable()
				}
			},
			immediate: true, // Executa ao montar o componente, se já houver valor
		},
	},

	mounted() {
		// TO-DO: vou precisar do id da sales_table pra saber de qual vou pegar
		//this.installmentDate = this.product.availability.sales_table[0].installment_dates

		this.$root.$on('update:installmentList', (newList) => {
			this.installmentList = newList
			this.validateForm()
		})

		this.$root.$on('modal-open-config-date-proposal', () =>{
			this.processSalesTable()
		})

		this.$root.$on('modal-close-config-date-proposal', () =>{
			this.resetForm()
		})
	},

	methods: {
		...mapActions('salesTable', ['updateSalesTableInstallmentDates']),

		resetForm() {
			this.installmentList = []
			this.residualData = {}
			this.validationErrors = []
			this.isValid = false
		},

		processSalesTable() {
			/*
			* Basicamente era para vir apenas as "datas da parcela" da API, mas colocaram junto do mesmo array uma config diferente que é as "data de resíduo", 
			* então toda vez que o dado chega tem que ser separado, oque é data da parcela normal e oque é data de residuo, e quando enviado deve juntar novamente
			*/
			const rawInstallments = this.salesTable.installments_date || []

			// Verifica se há parcelas "datas da parcela", por padrão sempre vem "data de residuo", mas só vem "datas da parcela" se estiverem cadastradas
			const hasRealInstallments = rawInstallments.some(item => item.type !== 'residue_date')

			// Se o valor veio da API, utiliza-o; caso contrário, usa o valor padrão.
			let installmentsDate = hasRealInstallments
				? rawInstallments.map(item => ({ ...this.defaultInstallment, ...item }))
				: [{ ...this.defaultInstallment }]

			// Retira o "data de residuo" do array
			this.installmentList = installmentsDate
				.filter(item => item.type !== 'residue_date')
				.map(item => ({ ...this.defaultInstallment, ...item }))

			// Separa o "data de residuo"
			const residualItem = rawInstallments.find(item => item.type === 'residue_date')
			this.residualData = residualItem
				? { ...this.defaultResidual, ...residualItem }
				: { ...this.defaultResidual }
		},

	    validateForm() {
	      this.isValid = true;
	      this.validationErrors = [];
	  
	      this.validateEmptyForm();
	      this.validateInstallments();
	      this.validateResidualData();
	  
	      if (this.validationErrors.length > 0) {
	        this.isValid = false;
	      }
	  
	      if (this.isValid && this.residualData.type) {
	        this.$root.$emit('clear-errors');
	      }

	      return this.isValid;
	    },

		addValidationError(message) {
  		  if (!this.validationErrors.includes(message)) {
  		    this.validationErrors = [...this.validationErrors, message];
  		  }
  		},
	  
	    areAllInstallmentsEmpty() {
	      return this.installmentList.every(installment => (
	        !installment.type &&
	        !installment.interest_date &&
	        !installment.readjustment_date &&
	        !installment.installment_ref &&
	        !installment.interest_date_month &&
	        !installment.readjustment_date_month
	      ));
	    },
	  
	    isResidualDataEmpty() {
	      return !this.residualData.type ||
	        (this.residualData.type === 'residue_date' &&
	         !this.residualData.date &&
	         !this.residualData.quantity_months);
	    },
	  
	    validateEmptyForm() {
	      if (this.areAllInstallmentsEmpty() && this.isResidualDataEmpty()) {
	        this.$root.$emit('clear-errors');
	      }
	    },
	  
	    validateInstallments() {
	      if (this.areAllInstallmentsEmpty()) return;
	  
	      if (this.hasMissingType()) {
			this.addValidationError(this.errorMessages.missingType)
	        this.isValid = false;
	        return;
	      }
	  
	      this.validateInstallmentFields();
	      this.checkCircularReferences();
	    },
	  
	    hasMissingType() {
	      return this.installmentList.some(installment => !installment.type);
	    },
	  
	    validateInstallmentFields() {
	      let hasInvalidInstallments = false;
	  
	      for (const installment of this.installmentList) {
	        if (!this.hasAtLeastOneField(installment)) {
			  this.addValidationError(this.errorMessages.atLeastOneField)

	          hasInvalidInstallments = true;
	          continue;
	        }
	  
	        if (installment.interest_date === 'month_diff' && !this.isValidMonth(installment.interest_date_month)) {
			  this.addValidationError(this.errorMessages.invalidMonths)

	          hasInvalidInstallments = true;
	        }
	  
	        if (installment.readjustment_date === 'month_diff' && !this.isValidMonth(installment.readjustment_date_month)) {
			  this.addValidationError(this.errorMessages.invalidMonths)

	          hasInvalidInstallments = true;
	        }
	      }
	  
	      if (hasInvalidInstallments) {
	        this.isValid = false;
	      }
	    },
	  
	    hasAtLeastOneField(installment) {
	      return installment.interest_date || 
	             installment.readjustment_date || 
	             installment.installment_ref;
	    },
	  
	    isValidMonth(monthValue) {
	      return monthValue && monthValue !== 0;
	    },
	  
		checkCircularReferences() {
		  // Cria um mapa para armazenar quem referencia quem
		  const referenceMap = {};

		  // Primeiro passo: montar o mapa de referências
		  for (const installment of this.installmentList) {
		    if (installment.installment_ref) {
		      referenceMap[installment.type] = installment.installment_ref;
		    }
		  }

		  // Segundo passo: verificar referências circulares
		  for (const type in referenceMap) {
		    const referencedType = referenceMap[type];

		    // Se o tipo referenciado também referencia o tipo original, temos uma referência circular
		    if (referenceMap[referencedType] === type) {
		      this.addValidationError(this.errorMessages.circularRefs);
		      return true; // Retorna true se encontrar pelo menos uma circular
		    }
		  }

		  return false; // Retorna false se não encontrar referências circulares
		},
	  
	    validateResidualData() {
	      if (this.isResidualDataEmpty()) return;
	  
	      if (this.residualData.type === 'residue_date' && 
	          this.residualData.date === 'month_diff' && !this.isValidMonth(this.residualData.quantity_months)) {

			this.addValidationError(this.errorMessages.invalidMonths)
	        this.isValid = false;
	      }
	    },

		async submit() {
			this.load = true

			// Caso não seja preenchido o campo de data, é enviado com valores zerados
			const residual = !this.residualData.date 
  				? { ...this.residualData, quantity_months: 0, days_of_the_month: 0 } 
  				: this.residualData;

			const data = { installments_date: [...this.installmentList, residual] }
			await salesTable
				.updateInstallmentDates(this.salesTable.id, data)
				.then((response) => {
					this.loadSuccess = true
					this.$mx_alertSuccess(response?.data?.msg)

					setTimeout(() => this.$root.$emit('modal-close-config-date-proposal'), 2000)

					this.updateSalesTableInstallmentDates(data.installments_date)
					this.isValid = false
				})
				.catch((error) => {
					this.loadFail = true
					if (error?.data?.msg) this.$mx_alertError(error.data.msg)
				})
				.finally(() => {
					setTimeout(() => {
						this.load = this.loadSuccess = this.loadFail = false
					}, 2500)
				})
		},
	},
}
</script>

<style lang="stylus" scoped>
@import './styles.styl'
</style>